import React, { useEffect, useCallback, useState } from 'react';
import { useWindows } from '../../contexts/WindowContext';
import { useSound } from '../../contexts/SoundContext';
import { useDraggable } from '../../hooks/useDraggable';
import { useResizable } from '../../hooks/useResizable';
import { Icon } from '../Icons';
import { SnapPreview } from './SnapPreview';
import type { WindowConfig, SnapZone, Position } from '../../types';
import { TASKBAR_HEIGHT } from '../../utils/constants';
import styles from './Window.module.css';

type AnimationState = 'none' | 'minimizing' | 'restoring' | 'maximizing';

interface WindowProps {
  config: WindowConfig;
  children: React.ReactNode;
}

export const Window: React.FC<WindowProps> = ({ config, children }) => {
  const {
    id,
    title,
    icon,
    state,
    position,
    size,
    minSize,
    zIndex,
    isActive,
    resizable = true,
    maximizable = true,
  } = config;

  const { closeWindow, minimizeWindow, maximizeWindow, restoreWindow, focusWindow, updatePosition, updateSize, snapWindow } = useWindows();
  const { playSound } = useSound();

  const [animationState, setAnimationState] = useState<AnimationState>('none');
  const [prevState, setPrevState] = useState(state);
  const [shouldRender, setShouldRender] = useState(state !== 'minimized');

  const isMaximized = state === 'maximized';

  // Handle state transitions with animations
  useEffect(() => {
    if (prevState !== state) {
      if (state === 'minimized' && prevState !== 'minimized') {
        // Minimizing - animate then hide
        setAnimationState('minimizing');
        setShouldRender(true);
        const timer = setTimeout(() => {
          setAnimationState('none');
          setShouldRender(false);
        }, 250);
        setPrevState(state);
        return () => clearTimeout(timer);
      } else if (prevState === 'minimized' && state !== 'minimized') {
        // Restoring from minimized - show then animate
        setShouldRender(true);
        setAnimationState('restoring');
        const timer = setTimeout(() => {
          setAnimationState('none');
        }, 250);
        setPrevState(state);
        return () => clearTimeout(timer);
      } else if (state === 'maximized' && prevState === 'normal') {
        // Maximizing
        setAnimationState('maximizing');
        const timer = setTimeout(() => {
          setAnimationState('none');
        }, 150);
        setPrevState(state);
        return () => clearTimeout(timer);
      }
      setPrevState(state);
    }
  }, [state, prevState]);

  // Handle drag end with snap zone detection
  const handleDragEnd = useCallback((pos: Position, detectedSnapZone: SnapZone) => {
    if (detectedSnapZone) {
      // Snap the window to the zone, saving current position/size for restoration
      snapWindow(id, detectedSnapZone, position, size);
    } else {
      updatePosition(id, pos);
    }
  }, [id, position, size, snapWindow, updatePosition]);

  // Draggable hook
  const {
    position: dragPosition,
    isDragging,
    snapZone: currentSnapZone,
    dragHandleProps,
    setPosition,
  } = useDraggable({
    initialPosition: position,
    disabled: isMaximized,
    onDragEnd: handleDragEnd,
  });

  // Resizable hook
  const {
    size: resizeSize,
    resizedPosition,
    isResizing,
    resizeHandleProps,
    setSize,
  } = useResizable({
    initialSize: size,
    minSize,
    position: dragPosition,
    disabled: isMaximized || !resizable,
    onResizeEnd: (newSize, newPos) => {
      updateSize(id, newSize);
      updatePosition(id, newPos);
    },
  });

  // Update position when config changes (e.g., when restored from maximized)
  useEffect(() => {
    if (!isDragging && !isResizing) {
      setPosition(position);
      setSize(size);
    }
  }, [position, size, isDragging, isResizing, setPosition, setSize]);

  const handleFocus = useCallback(() => {
    if (!isActive) {
      focusWindow(id);
      playSound('click');
    }
  }, [id, isActive, focusWindow, playSound]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeWindow(id);
    playSound('close');
  }, [id, closeWindow, playSound]);

  const handleMinimize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    minimizeWindow(id);
    playSound('minimize');
  }, [id, minimizeWindow, playSound]);

  const handleMaximize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMaximized) {
      restoreWindow(id);
    } else {
      maximizeWindow(id);
    }
    playSound('maximize');
  }, [id, isMaximized, maximizeWindow, restoreWindow, playSound]);

  const handleDoubleClick = useCallback(() => {
    if (maximizable) {
      if (isMaximized) {
        restoreWindow(id);
      } else {
        maximizeWindow(id);
      }
      playSound('maximize');
    }
  }, [id, isMaximized, maximizable, maximizeWindow, restoreWindow, playSound]);

  // Handle keyboard
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeWindow(id);
    }
  }, [id, closeWindow]);

  if (!shouldRender) return null;

  // Get animation class
  const getAnimationClass = () => {
    switch (animationState) {
      case 'minimizing': return styles.minimizing;
      case 'restoring': return styles.restoring;
      case 'maximizing': return styles.maximizing;
      default: return '';
    }
  };

  const windowStyle: React.CSSProperties = isMaximized
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: `calc(100% - ${TASKBAR_HEIGHT}px)`,
        zIndex,
      }
    : {
        position: 'absolute',
        left: isResizing ? resizedPosition.x : dragPosition.x,
        top: isResizing ? resizedPosition.y : dragPosition.y,
        width: resizeSize.width,
        height: resizeSize.height,
        zIndex,
      };

  return (
    <div
      className={`${styles.window} ${isActive ? styles.active : styles.inactive} ${isDragging ? styles.dragging : ''} ${getAnimationClass()}`}
      style={windowStyle}
      onMouseDown={handleFocus}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-label={title}
      tabIndex={-1}
    >
      {/* Title Bar */}
      <div
        className={styles.titleBar}
        {...dragHandleProps}
        onDoubleClick={handleDoubleClick}
      >
        <div className={styles.titleLeft}>
          <Icon name={icon} size={16} className={styles.titleIcon} />
          <span className={styles.titleText}>{title}</span>
        </div>
        <div className={styles.titleButtons} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          <button
            className={`${styles.titleButton} ${styles.minimize}`}
            onClick={handleMinimize}
            aria-label="Minimize"
            tabIndex={0}
          >
            <svg width="12" height="12" viewBox="0 0 12 12">
              <rect x="2" y="9" width="8" height="2" fill="currentColor"/>
            </svg>
          </button>
          {maximizable && (
            <button
              className={`${styles.titleButton} ${styles.maximize}`}
              onClick={handleMaximize}
              aria-label={isMaximized ? 'Restore' : 'Maximize'}
              tabIndex={0}
            >
              {isMaximized ? (
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <rect x="3" y="1" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1"/>
                  <rect x="3" y="1" width="8" height="2" fill="currentColor"/>
                  <rect x="1" y="4" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1"/>
                  <rect x="1" y="4" width="8" height="2" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <rect x="1" y="1" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1"/>
                  <rect x="1" y="1" width="10" height="2" fill="currentColor"/>
                </svg>
              )}
            </button>
          )}
          <button
            className={`${styles.titleButton} ${styles.close}`}
            onClick={handleClose}
            aria-label="Close"
            tabIndex={0}
          >
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Window Content */}
      <div className={styles.content}>
        {children}
      </div>

      {/* Resize Handles (only if resizable and not maximized) */}
      {resizable && !isMaximized && (
        <>
          <div {...resizeHandleProps('n')} />
          <div {...resizeHandleProps('s')} />
          <div {...resizeHandleProps('e')} />
          <div {...resizeHandleProps('w')} />
          <div {...resizeHandleProps('ne')} />
          <div {...resizeHandleProps('nw')} />
          <div {...resizeHandleProps('se')} />
          <div {...resizeHandleProps('sw')} />
        </>
      )}

      {/* Snap Preview (shown while dragging near screen edges) */}
      {isDragging && currentSnapZone && <SnapPreview zone={currentSnapZone} />}
    </div>
  );
};

export default Window;
